import { useEffect } from 'react';
import { NavLink, Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import Spinner from '../../components/Spinner';
import { useFosContext } from '../../hooks/useFosContext';
import type { FosContext } from '../../lib/fosApi';
import { Icon } from './ui';

export interface FosOutlet { context: FosContext; refresh: () => void }
export const useFos = () => useOutletContext<FosOutlet>();

/** The Friends of Scouting area: a hero band, its own section navigation, and the page below. Only sections that exist are shown. */
export default function FosShell() {
  const { context, loading, error, refresh } = useFosContext();
  const { tenantIds } = useAuth();
  void tenantIds;
  const { pathname } = useLocation();
  // Moving between Friends of Scouting pages starts at the top, not where the previous page was scrolled to.
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  if (loading && !context) return <div className="fos"><div className="fos-loading"><Spinner /></div></div>;
  if (error || !context) return <div className="fos"><div className="fos-error" role="alert">{error || 'Friends of Scouting is unavailable right now.'}</div></div>;

  const available = context.enabled && context.canViewDonors;
  return (
    <div className="fos">
      <header className="fos-hero">
        <div className="fos-hero-title">
          <span className="fos-hero-mark"><Icon name="heart" size={22} /></span>
          <div>
            <h1>Friends of Scouting</h1>
            <p>{context.enabled ? 'Donors, giving and progress for your council and districts.' : 'Not turned on for this council yet.'}</p>
          </div>
        </div>
        <nav className="fos-tabs" aria-label="Friends of Scouting sections">
          <NavLink to="/fos" end className={({ isActive }) => `fos-tab${isActive ? ' active' : ''}`}><Icon name="chart" size={15} />Dashboard</NavLink>
          {available
            ? <NavLink to="/fos/donors" className={({ isActive }) => `fos-tab${isActive ? ' active' : ''}`}><Icon name="users" size={15} />Donors</NavLink>
            : <span className="fos-tab disabled" aria-disabled="true"><Icon name="users" size={15} />Donors</span>}
          {context.enabled && context.capabilities.viewHistory && <NavLink to="/fos/contributions" className={({ isActive }) => `fos-tab${isActive ? ' active' : ''}`}><Icon name="dollar" size={15} />Contributions</NavLink>}
          {context.enabled && (context.capabilities.viewProgress || context.capabilities.administerGoals || context.capabilities.administerCampaigns) && <NavLink to="/fos/goals" className={({ isActive }) => `fos-tab${isActive ? ' active' : ''}`}><Icon name="target" size={15} />Fundraising Goals</NavLink>}
          {context.enabled && context.capabilities.viewFollowUps && <NavLink to="/fos/follow-ups" className={({ isActive }) => `fos-tab${isActive ? ' active' : ''}`}><Icon name="clock" size={15} />Follow-ups</NavLink>}
        </nav>
      </header>
      <div className="fos-body"><Outlet context={{ context, refresh } satisfies FosOutlet} /></div>
    </div>
  );
}
